import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useContext, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { BsCaretDownFill, BsCaretRightFill, BsMap } from 'react-icons/bs';
import { Link } from 'react-router-dom';

import { useCodeDefinitionsAsMaps } from '../../../../v2/js/hooks/useCodeDefinitions';
import { formatApiDate } from '../../../../v2/js/utils/date';
import {
  ClearanceRejectionReasonEnum,
  ClearanceStatusEnum,
  ConnectedFacility,
  ConnectionRequestStatus,
  FacilityRejectionReasonEnum,
  FacilityStatusEnum,
  ProviderRejectionReasonEnum,
  UserTypeEnum,
  ConnectedFacilityRateHistory,
  ConnectedOpportunity,
  HealthcareEntitiesService,
} from '../../../api';
import { createGoogleMapsLink } from '../../../utils/address';
import { parseDate } from '../../../utils/date';
import { newLineToBr } from '../../../utils/string';
import { SessionContext } from '../../contexts/SessionContext';
import { getConnectionSenderName } from '../../utils/connections';
import { Card } from '../Card';
import { ClearanceBadge } from '../ClearanceBadge';
import { ConnectionStatusBadge } from '../ConnectionStatusBadge';
import { PresentationBadge } from '../PresentationBadge';
import { RateDisplay } from '../ProviderProfileHeader/components';
import { ProviderRepresentation } from '../ProviderRepresentation';

const REJECTION_REASONS = window.REJECTION_REASONS;
const CLEARANCE_REJECTION_REASONS = window.CLEARANCES_REJECTION_REASONS;
const FACILITY_REJECTION_REASONS = window.FACILITY_REJECTION_REASONS;

const DATE_FORMAT = 'LLL d, yyyy';

interface ConnectionRequestCardProps {
  connectionRequest: ConnectedFacility;
  onAccept: (connection: ConnectedFacility) => void;
  onReject: (connection: ConnectedFacility) => void;
  onCancel: (connection: ConnectedFacility) => void;
  needsContractSigned?: boolean;
  onOpenContractModal?: () => void;
  openedFromProfile?: boolean;
}

const FacilityAddress = ({
  facility,
  userIsProvider,
}: {
  facility: ConnectedFacility['facility'];
  userIsProvider: boolean;
}) => {
  if (!facility.street_address && !facility.city && !facility.state) return null;

  return (
    <div className="text-muted mb-2">
      <small className="d-block pl-2">
        <div className="d-flex justify-content-between align-items-center">
          <div className={`${userIsProvider ? 'mt-2' : ''}`}>
            <div>{facility.healthcare_entity.name}</div>
            {facility.street_address}
            {(facility.city || facility.state || facility.zip_code) && (
              <div className="mt-0">
                {[facility.city, facility.state, facility.zip_code].filter(Boolean).join(' ')}
              </div>
            )}
          </div>
          <a
            href={createGoogleMapsLink(
              facility.street_address || '',
              facility.city,
              facility.state,
              facility.zip_code
            )}
            target="_blank"
            rel="noopener noreferrer"
            className={`d-flex align-items-center btn btn-outline-primary btn-sm pt-1 text-primary-dark ${
              userIsProvider ? 'mt-2' : ''
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <BsMap className="me-2" />
            Map
          </a>
        </div>
      </small>
    </div>
  );
};

const FacilityDetails = ({
  showDetails,
  opportunity,
  facilityId,
  codeDefinitions,
}: {
  showDetails: boolean;
  opportunity?: ConnectedOpportunity;
  facilityId?: string;
  codeDefinitions?: Record<string, Record<string, string>>;
}) => {
  const { data: detailedFacility } = useQuery({
    queryKey: ['facility', facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesRetrieve({ id: facilityId! }),
    enabled: !!facilityId && showDetails,
  });

  const { data: facilitySpecialties } = useQuery({
    queryKey: ['facility-specialties', facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesList({
        facility: facilityId!,
      }),
    enabled: !!facilityId && showDetails,
  });

  if (!showDetails) return null;

  function getDisplayValueForField(field: any) {
    let displayValue = field.field_value;

    if (field.field_type === 'boolean' && typeof field.field_value === 'boolean') {
      return field.field_value ? 'Yes' : 'No';
    }

    if (field.field_type === 'multiselect' && Array.isArray(field.field_value)) {
      if (Array.isArray(field.choices) && field.choices.length > 0) {
        displayValue = field.field_value
          .map((val: string) => {
            const match = field.choices.find((choice: any) => choice.code === val);
            return match ? match.title : val;
          })
          .join(', ');
      } else {
        displayValue = field.field_value.join(', ');
      }
    }

    return displayValue;
  }

  return (
    <div className="text-muted mt-2 border p-2 rounded">
      <style>
        {`
          .responsive-column {
            width: 75%
          }
          @media (min-width: 768px) {
            .responsive-column {
              width: 40%
            }
          }
        `}
      </style>
      <div className="mt-2">
        <h6 className="mb-2">
          {detailedFacility?.facility_type && (
            <>
              {codeDefinitions?.FacilityTypes?.[detailedFacility?.facility_type] ||
                detailedFacility?.facility_type}{' '}
            </>
          )}
          Facility
        </h6>
        {(() => {
          const allFields: Array<{ field_name: string; field_title: string; field_value: any }> =
            [];

          if (detailedFacility?.emr) {
            allFields.push({
              field_name: 'emr',
              field_title: 'EMR',
              field_value: detailedFacility.emr,
            });
          }

          if (detailedFacility?.facility_type_fields) {
            try {
              const fields =
                typeof detailedFacility.facility_type_fields === 'string'
                  ? JSON.parse(detailedFacility.facility_type_fields)
                  : detailedFacility.facility_type_fields;

              const filteredFields = fields.filter(
                (field: any) => field.show_on_connection !== false && field.field_value
              );

              if (Array.isArray(filteredFields) && filteredFields.length > 0) {
                filteredFields.forEach((field: any) => {
                  allFields.push({
                    field_name: field.field_name,
                    field_title: field.field_title,
                    field_value: getDisplayValueForField(field),
                  });
                });
              }
            } catch (error) {
              console.error('Error parsing facility_type_fields:', error);
            }
          }

          if (allFields.length >= 6) {
            const midPoint = Math.ceil(allFields.length / 2);
            const leftColumnFields = allFields.slice(0, midPoint);
            const rightColumnFields = allFields.slice(midPoint);

            return (
              <div className="row">
                <div className="col-md-6 responsive-column">
                  {leftColumnFields.map((field) => (
                    <div key={field.field_name} className="d-flex justify-content-between mb-1">
                      <small>
                        <strong>{field.field_title}:</strong>
                      </small>
                      <small className="text-end">{field.field_value}</small>
                    </div>
                  ))}
                </div>
                <div className="col-md-6 responsive-column">
                  {rightColumnFields.map((field) => (
                    <div key={field.field_name} className="d-flex justify-content-between mb-1">
                      <small>
                        <strong>{field.field_title}:</strong>
                      </small>
                      <small className="text-end">{field.field_value}</small>
                    </div>
                  ))}
                </div>
              </div>
            );
          } else {
            return (
              <div className="responsive-column">
                {allFields.map((field) => (
                  <div key={field.field_name} className="d-flex justify-content-between mb-1">
                    <small>
                      <strong>{field.field_title}:</strong>
                    </small>
                    <small className="text-end">{field.field_value}</small>
                  </div>
                ))}
              </div>
            );
          }
        })()}

        {facilitySpecialties?.results &&
          Array.isArray(facilitySpecialties.results) &&
          facilitySpecialties.results.length > 0 &&
          opportunity?.specialty &&
          facilitySpecialties.results.some(
            (specialty: any) => specialty.specialty === opportunity.specialty
          ) && (
            <div className="mt-3 pt-3 border-top">
              {facilitySpecialties.results
                .filter((specialty: any) => specialty.specialty === opportunity.specialty)
                .map((specialty: any) => (
                  <div key={specialty.id} className="mb-3">
                    <h6 className="mb-2">
                      {codeDefinitions?.Specialty?.[specialty.specialty] || specialty.specialty}
                    </h6>
                    {specialty.dynamic_fields &&
                      (() => {
                        try {
                          const fields =
                            typeof specialty.dynamic_fields === 'string'
                              ? JSON.parse(specialty.dynamic_fields)
                              : specialty.dynamic_fields;

                          const filteredFields = fields.filter(
                            (field: any) => field.show_on_connection !== false && field.field_value
                          );

                          if (Array.isArray(filteredFields) && filteredFields.length > 0) {
                            if (filteredFields.length >= 6) {
                              const midPoint = Math.ceil(filteredFields.length / 2);
                              const leftColumnFields = filteredFields.slice(0, midPoint);
                              const rightColumnFields = filteredFields.slice(midPoint);

                              return (
                                <div className="row">
                                  <div className="col-md-6 responsive-column">
                                    {leftColumnFields.map((field: any) => (
                                      <div
                                        key={field.field_name}
                                        className="d-flex justify-content-between mb-1"
                                      >
                                        <small>
                                          <strong>{field.field_title}:</strong>
                                        </small>
                                        <small className="text-end">
                                          {getDisplayValueForField(field)}
                                        </small>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="col-md-6 responsive-column">
                                    {rightColumnFields.map((field: any) => (
                                      <div
                                        key={field.field_name}
                                        className="d-flex justify-content-between mb-1"
                                      >
                                        <small>
                                          <strong>{field.field_title}:</strong>
                                        </small>
                                        <small className="text-end">
                                          {getDisplayValueForField(field)}
                                        </small>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div className="responsive-column">
                                  {filteredFields.map((field: any) => (
                                    <div
                                      key={field.field_name}
                                      className="d-flex justify-content-between mb-1"
                                    >
                                      <small>
                                        <strong>{field.field_title}:</strong>
                                      </small>
                                      <small className="text-end">
                                        {getDisplayValueForField(field)}
                                      </small>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                          }
                          return null;
                        } catch (error) {
                          console.error('Error parsing specialty dynamic_fields:', error);
                          return null;
                        }
                      })()}
                  </div>
                ))}
            </div>
          )}
      </div>

      {opportunity && (
        <div className="mt-3 pt-3 border-top">
          <h6 className="mb-2">Opportunity Specifics</h6>
          {opportunity.starts_on && (
            <div className="mb-1">
              <small>
                <strong>Start Date: </strong>
                <span className="text-muted">
                  {formatApiDate(opportunity.starts_on, { dateFormat: DATE_FORMAT })}
                </span>
              </small>
            </div>
          )}
          {opportunity.estimated_duration && (
            <div className="mb-1">
              <small>
                <strong>Duration: </strong>
              </small>
              <small className="text-end">{opportunity.estimated_duration}</small>
            </div>
          )}
          {opportunity.description && (
            <div className="mb-1">
              <small>
                <strong>Description:</strong>
                <div className="mt-1">{opportunity.description}</div>
              </small>
            </div>
          )}
          {opportunity.additional_info && (
            <div className="mb-1">
              <small>
                <strong>Additional Info:</strong>
                <div
                  className="mt-1"
                  dangerouslySetInnerHTML={{
                    __html: newLineToBr(opportunity.additional_info),
                  }}
                />
              </small>
            </div>
          )}
        </div>
      )}
      {detailedFacility?.extra_info && (
        <div className="mt-3 pt-3 border-top">
          <small>
            <div
              dangerouslySetInnerHTML={{
                __html: newLineToBr(detailedFacility?.extra_info || ''),
              }}
            />
          </small>
        </div>
      )}
    </div>
  );
};

const RejectionAlert = ({ connectionRequest }: { connectionRequest: ConnectedFacility }) => {
  if (connectionRequest?.status !== ConnectionRequestStatus.REJECTED) return null;

  return (
    <Alert variant="danger" className="mt-3 p-2">
      <div className="p-1">
        <small>Rejected because</small>
        <span className="d-block">
          {connectionRequest.provider_rejection_reason === ProviderRejectionReasonEnum.OTHER
            ? connectionRequest.provider_rejection_details ||
              REJECTION_REASONS[ProviderRejectionReasonEnum.OTHER]
            : REJECTION_REASONS[
                connectionRequest.provider_rejection_reason ?? ProviderRejectionReasonEnum.OTHER
              ]}
        </span>
      </div>
    </Alert>
  );
};

const ClearanceAlert = ({ connectionRequest }: { connectionRequest: ConnectedFacility }) => {
  if (connectionRequest?.clearance_status !== ClearanceStatusEnum.NOT_CLEARED) return null;

  return (
    <Alert variant="danger" className="mt-3 p-2">
      <div className="p-1">
        <small>Not cleared because</small>
        <span className="d-block">
          {connectionRequest.clearance_rejection_reason === ClearanceRejectionReasonEnum.OTHER
            ? connectionRequest.clearance_rejection_details ||
              CLEARANCE_REJECTION_REASONS[ClearanceRejectionReasonEnum.OTHER]
            : CLEARANCE_REJECTION_REASONS[
                connectionRequest.clearance_rejection_reason ?? ClearanceRejectionReasonEnum.OTHER
              ]}
        </span>
      </div>
    </Alert>
  );
};

const FacilityStatusAlert = ({ connectionRequest }: { connectionRequest: ConnectedFacility }) => {
  if (connectionRequest.facility_status !== FacilityStatusEnum.DENIED) return null;

  return (
    <Alert variant="danger" className="mt-3 p-2">
      <div className="p-1">
        <small>Facility denied because</small>
        <span className="d-block">
          {connectionRequest.facility_rejection_reason === FacilityRejectionReasonEnum.OTHER &&
          connectionRequest.facility_rejection_details
            ? connectionRequest.facility_rejection_details
            : FACILITY_REJECTION_REASONS[
                connectionRequest.facility_rejection_reason ?? FacilityRejectionReasonEnum.OTHER
              ]}
        </span>
      </div>
    </Alert>
  );
};

const ActionButtons = ({
  connectionRequest,
  userIsProvider,
  requiresProviderResponse,
  onAccept,
  onReject,
  onCancel,
  needsContractSigned,
  onOpenContractModal,
  openedFromProfile,
}: {
  connectionRequest: ConnectedFacility;
  userIsProvider: boolean;
  requiresProviderResponse: boolean;
  onAccept: (connection: ConnectedFacility) => void;
  onReject: (connection: ConnectedFacility) => void;
  onCancel: (connection: ConnectedFacility) => void;
  needsContractSigned?: boolean;
  onOpenContractModal?: () => void;
  openedFromProfile: boolean;
}) => {
  if (!requiresProviderResponse) return null;

  if (userIsProvider) {
    if (needsContractSigned) {
      return (
        <div className="d-flex flex-column align-items-end gap-2">
          <div className="text-warning pb-3 px-1" style={{ fontSize: '14px' }}>
            You must sign the Professional Services Agreement in order to move forward with this
            request.{' '}
            <button
              onClick={onOpenContractModal}
              className="alert-link border-0 bg-transparent p-0"
              style={{ textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
            >
              Sign now
            </button>
          </div>
          <div className="d-flex w-100 justify-content-between justify-content-sm-end align-items-center gap-3">
            <Button variant="danger" onClick={() => onReject(connectionRequest)}>
              Not Interested
            </Button>
            <Button className="disabled cursor-not-allowed" variant="primary" disabled>
              I&apos;m Interested
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex w-100 justify-content-between justify-content-sm-end align-items-center gap-3">
        <Button variant="danger" onClick={() => onReject(connectionRequest)}>
          Not Interested
        </Button>
        <Button variant="primary" onClick={() => onAccept(connectionRequest)}>
          I&apos;m Interested
        </Button>
      </div>
    );
  }

  return (
    <>
      {!openedFromProfile && (
        <div className="w-100 d-flex justify-content-end">
          <Button variant="outline-danger" onClick={() => onCancel(connectionRequest)}>
            Cancel Request
          </Button>
        </div>
      )}
    </>
  );
};

const FacilityLink = ({ facility }: { facility: ConnectedFacility['facility'] }) => {
  const { user } = useContext(SessionContext);
  const isProvider = user?.user_type === UserTypeEnum.PROVIDER;

  const facilityUrl = isProvider
    ? `/app/provider/facility-details/${facility.id}`
    : `/app/organization/facilities/${facility.id}`;

  return <Link to={facilityUrl}>{facility.name}</Link>;
};

export const ConnectionRequestCard = ({
  connectionRequest,
  onAccept,
  onReject,
  onCancel,
  needsContractSigned,
  onOpenContractModal,
  openedFromProfile = false, // eslint-disable-next-line sonarjs/cognitive-complexity
}: ConnectionRequestCardProps) => {
  const { user } = useContext(SessionContext);
  const userIsProvider = user?.user_type === UserTypeEnum.PROVIDER;
  const requiresProviderResponse =
    connectionRequest.status === ConnectionRequestStatus.PENDING_RESPONSE ||
    connectionRequest.status === ConnectionRequestStatus.PENDING_SEND;
  // this was hiding information about an opportunity based on a couple of facility fields having values
  // so defaulting to true for now
  const hasFacilityDetails = true;
  const [showFacilityDetails, setShowFacilityDetails] = useState(
    userIsProvider && requiresProviderResponse
  );
  const [showRateHistory, setShowRateHistory] = useState(false);

  const proposedRates = {
    dayRate: userIsProvider
      ? connectionRequest.shift_rate_day
      : connectionRequest.facility_rate_day,
    nightRate: userIsProvider
      ? connectionRequest.shift_rate_night
      : connectionRequest.facility_rate_night,
    _24HRate: userIsProvider
      ? connectionRequest.shift_rate_24h
      : connectionRequest.facility_rate_24h,
    callRate: userIsProvider
      ? connectionRequest.shift_rate_call
      : connectionRequest.facility_rate_call,
  };

  const originalRateData = connectionRequest.rate_history?.find(
    (rate) => rate.reason_code === 'ORIGINAL'
  );

  const originalRates = {
    dayRate: userIsProvider
      ? originalRateData?.shift_rate_day
      : originalRateData?.facility_rate_day,
    nightRate: userIsProvider
      ? originalRateData?.shift_rate_night
      : originalRateData?.facility_rate_night,
    _24HRate: userIsProvider
      ? originalRateData?.shift_rate_24h
      : originalRateData?.facility_rate_24h,
    callRate: userIsProvider
      ? originalRateData?.shift_rate_call
      : originalRateData?.facility_rate_call,
  };

  const hasRateHistory =
    connectionRequest.rate_history && connectionRequest.rate_history.length > 0;

  const getRateValue = (rate: ConnectedFacilityRateHistory, rateType: string) => {
    const key = userIsProvider ? `shift_rate_${rateType}` : `facility_rate_${rateType}`;
    const value = rate[key as keyof ConnectedFacilityRateHistory];
    return typeof value === 'number' && value > 0 ? value : null;
  };

  const getRateTypes = (rateHistory: ConnectedFacilityRateHistory[]) => {
    const prefix = userIsProvider ? 'shift_rate_' : 'facility_rate_';
    const types = ['day', 'night', 'call', '24h'];
    return types.filter((type) =>
      rateHistory.some((rate) => {
        const key = `${prefix}${type}` as keyof ConnectedFacilityRateHistory;
        return rate[key] !== null;
      })
    );
  };

  const { data: codeDefinitions } = useCodeDefinitionsAsMaps([
    'RateChangeReason',
    'FacilityTypes',
    'Specialty',
  ]);

  const rateChangeReasons = codeDefinitions?.RateChangeReason || {};

  return (
    <Card className="p-3 h-100 d-flex flex-column justify-content-start">
      <div className="d-flex justify-content-between mb-3">
        <ConnectionStatusBadge status={connectionRequest.status!} userIsProvider={userIsProvider} />
        <small>Updated {format(parseISO(connectionRequest.updated_at!), DATE_FORMAT)}</small>
      </div>

      <div className="d-flex justify-content-between align-items-start">
        <h5 className="mb-0 me-3 text-break flex-grow-1">
          <FacilityLink facility={connectionRequest.facility} />
        </h5>
        {connectionRequest.clearance_status && (
          <div className="flex-shrink-0">
            <ClearanceBadge status={connectionRequest.clearance_status as ClearanceStatusEnum} />
          </div>
        )}
      </div>
      {!userIsProvider && (
        <div className="line-height-tight text-section-name--dark-brand text-medium">
          Sent By: {getConnectionSenderName(connectionRequest)}
        </div>
      )}
      <FacilityAddress facility={connectionRequest.facility} userIsProvider={userIsProvider} />

      {!userIsProvider && (
        <div>
          <small>Provider</small>
          <ProviderRepresentation provider={connectionRequest.provider} />
        </div>
      )}

      {connectionRequest.message && (
        <div className={`mt-3 ${connectionRequest.message ? 'fst-italic' : ''}`}>
          <small className="text-muted">Message</small>
          <div>{connectionRequest.message}</div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center">
        <RateDisplay
          isProviderLoading={false}
          isCurrentProvider={userIsProvider}
          rates={proposedRates}
          originalRates={originalRates}
          minHeight={0}
          showRateComparison={
            connectionRequest.status === ConnectionRequestStatus.PENDING_RESPONSE ||
            connectionRequest.status === ConnectionRequestStatus.PENDING_SEND
          }
        />
      </div>

      {hasFacilityDetails && (
        <div className="mt-3">
          <Button
            variant="link"
            className="p-0 text-decoration-none d-flex align-items-center gap-2"
            onClick={() => setShowFacilityDetails(!showFacilityDetails)}
          >
            {showFacilityDetails ? <BsCaretDownFill /> : <BsCaretRightFill />}
            <span>{showFacilityDetails ? 'Hide' : 'Show'} More Details</span>
          </Button>

          <FacilityDetails
            showDetails={showFacilityDetails}
            opportunity={connectionRequest.opportunity}
            facilityId={connectionRequest.facility.id}
            codeDefinitions={codeDefinitions}
          />
        </div>
      )}

      {hasRateHistory && (
        <div className="mt-3">
          <Button
            variant="link"
            className="p-0 text-decoration-none d-flex align-items-center gap-2"
            onClick={() => setShowRateHistory(!showRateHistory)}
          >
            {showRateHistory ? <BsCaretDownFill /> : <BsCaretRightFill />}
            <span>{showRateHistory ? 'Hide' : 'Show'} Connection Rate History</span>
          </Button>

          {showRateHistory && (
            <div className="mt-2 table-responsive">
              <table className="table table-sm fs-6 fs-sm-small">
                <thead>
                  <tr className="text-nowrap">
                    <th>
                      <span className="d-none d-sm-inline">Effective </span>Date
                    </th>
                    {getRateTypes(connectionRequest.rate_history).map((type) => (
                      <th key={type} className="text-capitalize">
                        {type}
                      </th>
                    ))}
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody className="small">
                  {connectionRequest.rate_history.map((rate, index) => (
                    <tr key={index} className="text-nowrap">
                      <td>
                        {rate.effective_at
                          ? new Date(rate.effective_at).toLocaleDateString()
                          : '--'}
                      </td>
                      {getRateTypes(connectionRequest.rate_history).map((type) => (
                        <td key={type}>
                          {getRateValue(rate, type) !== null
                            ? `$${getRateValue(rate, type)}`
                            : '--'}
                        </td>
                      ))}
                      <td className="text-capitalize">
                        {rate.reason_code
                          ? rateChangeReasons?.[rate.reason_code] ||
                            rate.reason_code?.replace(/_/g, ' ')
                          : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <RejectionAlert connectionRequest={connectionRequest} />
      <ClearanceAlert connectionRequest={connectionRequest} />
      <FacilityStatusAlert connectionRequest={connectionRequest} />

      <div className="mt-auto">
        <Card.Divider cardPadding="1rem" />
        <div className="d-flex w-100 justify-content-between justify-content-sm-end align-items-center">
          {/* <div className="d-flex flex-column justify-content-center">
            <small>
              Created&nbsp;&nbsp;{format(parseISO(connectionRequest.created_at), DATE_FORMAT_SHORT)}
            </small>
            <small>
              Updated&nbsp;
              {format(parseISO(connectionRequest.updated_at), DATE_FORMAT_SHORT)}
            </small>
          </div> */}

          {!requiresProviderResponse &&
            userIsProvider &&
            connectionRequest.status !== ConnectionRequestStatus.CANCELED &&
            connectionRequest.status !== ConnectionRequestStatus.REJECTED &&
            connectionRequest.clearance_status === ClearanceStatusEnum.CLEARED && (
              <PresentationBadge
                className="p-2"
                isTransparent={false}
                status={connectionRequest.facility_status as FacilityStatusEnum}
              />
            )}

          {!requiresProviderResponse &&
            !userIsProvider &&
            connectionRequest.status !== ConnectionRequestStatus.REJECTED &&
            connectionRequest.status !== ConnectionRequestStatus.CANCELED &&
            connectionRequest.facility_status === FacilityStatusEnum.PENDING_APPROVAL && (
              <>
                {connectionRequest.clearance_status === ClearanceStatusEnum.PENDING_CLEARANCE && (
                  <Link to={`/app/healthcare-entity/providers/${connectionRequest.provider.id}`}>
                    <Button variant="warning" size="sm" className="btn-warning-light">
                      Review For Clearance
                    </Button>
                  </Link>
                )}
                {connectionRequest.clearance_status === ClearanceStatusEnum.CLEARED && (
                  <Link to={`/app/healthcare-entity/providers/${connectionRequest.provider.id}`}>
                    <Button size="sm" className="btn-warning-light">
                      Review For Approval
                    </Button>
                  </Link>
                )}
              </>
            )}

          {!requiresProviderResponse &&
            !userIsProvider &&
            connectionRequest.status !== ConnectionRequestStatus.REJECTED &&
            connectionRequest.facility_status !== FacilityStatusEnum.PENDING_APPROVAL && (
              <PresentationBadge
                className="p-2"
                isTransparent={false}
                status={connectionRequest.facility_status as FacilityStatusEnum}
              />
            )}

          {connectionRequest.clearance_status === ClearanceStatusEnum.NOT_CLEARED &&
            connectionRequest.clearance_date && (
              <small className="text-danger">
                Eligible {format(parseDate(connectionRequest.clearance_date), DATE_FORMAT)}
              </small>
            )}

          <ActionButtons
            connectionRequest={connectionRequest}
            userIsProvider={userIsProvider}
            requiresProviderResponse={requiresProviderResponse}
            onAccept={onAccept}
            onReject={onReject}
            onCancel={onCancel}
            needsContractSigned={needsContractSigned}
            onOpenContractModal={onOpenContractModal}
            openedFromProfile={openedFromProfile}
          />
        </div>
      </div>
    </Card>
  );
};
